import { shallowEqual } from 'fast-equals';

import { getFirstSelectedFacetValue } from 'components/search/FacetAccordion/FacetAccordion.utils';
import { SIZE_DRAWER_TITLE_BY_DEPARTMENT } from 'constants/quickFilter';
import { findFacetByField, ZC1FACET } from 'helpers/SearchUtils';
import type { FacetGroupType, NavigationItem } from 'types/search';

interface QuickFilterAttributes {
  quickFilterGroupId: string;
  quickFilterGroupDisplayName: string;
  quickFilterParentGroupId: string;
  quickFilterParentGroupDisplayName: string;
}

/**
 * Returns the complete backend Quick Filter grouping record for a navigation item.
 * Returns `undefined` unless the source is flagged and all four Quick Filter grouping fields are present.
 * Navigation `groupId`, `parentGroupId`, facet identity, and display copy are never fallback sources.
 */
export const getQuickFilterAttributes = (item: NavigationItem): QuickFilterAttributes | undefined => {
  const quickFilterSource = item.type === 'group' ? item.children.find(child => child.quickFilter) : item;

  if (
    quickFilterSource?.quickFilter !== true ||
    quickFilterSource.quickFilterGroupId === undefined ||
    quickFilterSource.quickFilterGroupDisplayName === undefined ||
    quickFilterSource.quickFilterParentGroupId === undefined ||
    quickFilterSource.quickFilterParentGroupDisplayName === undefined
  ) {
    return undefined;
  }

  return {
    quickFilterGroupId: quickFilterSource.quickFilterGroupId,
    quickFilterGroupDisplayName: quickFilterSource.quickFilterGroupDisplayName,
    quickFilterParentGroupId: quickFilterSource.quickFilterParentGroupId,
    quickFilterParentGroupDisplayName: quickFilterSource.quickFilterParentGroupDisplayName
  };
};

/**
 * Returns the ZC section the Drawer treats as active: the one section whose `quickFilterGroupDisplayName`
 * matches the pill's `quickFilterParentGroupDisplayName`, otherwise the last section.
 */
export const getActiveZetaCategorySection = (sections: NavigationItem[], parentGroupDisplayName: string): NavigationItem | undefined => {
  const matchingSections = sections.filter(section => getQuickFilterAttributes(section)?.quickFilterGroupDisplayName === parentGroupDisplayName);

  return matchingSections.length === 1 ? matchingSections[0] : sections[sections.length - 1];
};

/** Returns the IDs of the sections a Quick Filter Pill's Drawer opens with: each `isExpanded` section, plus the active one when present. */
export const getDefaultExpandedSections = (sections: NavigationItem[], activeSectionId?: string): Set<string> => {
  const expandedSections = new Set(sections.filter(section => section.isExpanded).map(section => section.facetField));

  if (activeSectionId && sections.some(section => section.facetField === activeSectionId)) {
    expandedSections.add(activeSectionId);
  }

  return expandedSections;
};

/** Returns every Size section ID when the sections all belong to one size group, so the Drawer can open them together. */
export const getSingleGroupDefaultExpandedSectionIds = (sections: NavigationItem[]): string[] => {
  if (sections.length < 2 || !sections.every(section => section.type === 'single')) {
    return [];
  }

  const quickFilterSectionGroupIds = sections.map(section => getQuickFilterAttributes(section)?.quickFilterGroupId);
  const [firstQuickFilterSectionGroupId] = quickFilterSectionGroupIds;

  if (
    firstQuickFilterSectionGroupId === undefined ||
    quickFilterSectionGroupIds.some(quickFilterSectionGroupId => quickFilterSectionGroupId !== firstQuickFilterSectionGroupId)
  ) {
    return [];
  }

  return sections.map(section => section.facetField);
};

/** Returns whether the default section IDs changed, comparing the IDs rather than the arrays, which are rebuilt for every response. */
export const hasDefaultExpandedSectionIdsChanged = (
  currentDefaultExpandedSectionIds?: readonly string[],
  previousDefaultExpandedSectionIds?: readonly string[]
): boolean => !shallowEqual(new Set(currentDefaultExpandedSectionIds), new Set(previousDefaultExpandedSectionIds));

/**
 * Resolves the Size pill's drawer title, or `fallbackTitle` when no contextual copy applies.
 *
 * @param sections the Size pill's own navigation items
 * @param navigation the full navigation, read only for the selected Department
 * @param fallbackTitle the pill's label
 */
export const getSizeDrawerTitle = (sections: NavigationItem[], navigation: NavigationItem[], fallbackTitle: string): string => {
  // A pill without sections has no contextual copy to derive.
  if (sections.length === 0) {
    return fallbackTitle;
  }

  const hasOnlyGroupedFacetSections = sections.every((section): section is FacetGroupType => section.type === 'group');

  // Only a fully grouped response carries contextual copy; flat and partially grouped sets keep the short title.
  if (!hasOnlyGroupedFacetSections) {
    return fallbackTitle;
  }

  const [firstGroup] = sections;

  // A single group titles the panel with its own display name, such as "Women's Clothing Size".
  if (sections.length === 1 && firstGroup) {
    return firstGroup.groupDisplayName || fallbackTitle;
  }

  // Several groups take the selected Department's title, such as "Shoe Size & Width".
  const departmentFacet = findFacetByField(navigation, ZC1FACET);
  const department = departmentFacet && getFirstSelectedFacetValue(departmentFacet)?.name?.toLowerCase();

  return (department ? SIZE_DRAWER_TITLE_BY_DEPARTMENT[department] : undefined) ?? fallbackTitle;
};
